import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  CircularProgress, 
  Container, 
  Typography, 
  Button, 
  IconButton,
  Stack,
  Chip,
  Card,
  CardMedia,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import { 
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  ShoppingCart as ShoppingCartIcon,
  MusicNote as MusicNoteIcon,
  ExplicitRounded as ExplicitIcon,
  QueueMusic as QueueIcon,
  PlaylistAdd as AddToPlaylistIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { fetchAlbum, fetchAlbumTracks } from '@/services/albumService';
import { useAudio } from '@/context/AudioProvider';
import { useAuth } from '@/context/AuthProvider';
import { Album, Track, Artist } from '@/types';
import { PLACEHOLDER_ALBUM, PLACEHOLDER_ARTIST } from '@/services/mediaService';
import { canPlayAlbum } from '@/services/accessService';
import { API_BASE_URL } from '@/config';
import MetaTagManager, { createAlbumMetaData } from '@/components/MetaTagManager';
import AddToMixtapeDialog from '@/components/mixtapes/AddToMixtapeDialog';

const formatDuration = (duration?: number | string) => {
  if (!duration) return "--:--";
  
  if (typeof duration === 'string' && duration.includes(':')) {
    return duration;
  }
  
  const durationNum = typeof duration === 'string' ? parseFloat(duration) : duration;
  const isMilliseconds = durationNum > 3600;
  const seconds = isMilliseconds ? Math.floor(durationNum / 1000) : Math.floor(durationNum);
  
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const AlbumPageBySlug: React.FC = () => {
  const { slug, id, artistIdentifier, albumIdentifier } = useParams<{ 
    slug?: string; 
    id?: string; 
    artistIdentifier?: string; 
    albumIdentifier?: string; 
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, token, hasActiveSubscription } = useAuth();
  const { playTrack, togglePlay, state } = useAudio();
  const { isPlaying, currentTrack } = state;

  const [album, setAlbum] = useState<Album | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [albumAccess, setAlbumAccess] = useState<any>(null);
  const [autoPlayTriggered, setAutoPlayTriggered] = useState(false);
  const [mixtapeDialog, setMixtapeDialog] = useState<{ open: boolean; trackId?: string }>({ open: false });

  // Determine what identifiers we have
  const isHierarchicalRoute = !!(artistIdentifier && albumIdentifier);
  const albumId = isHierarchicalRoute ? albumIdentifier : (id || slug);
  const artistId = artistIdentifier;

  console.log('Album page params:', { slug, id, artistIdentifier, albumIdentifier, isHierarchicalRoute, albumId, artistId });

  useEffect(() => {
    const loadAlbum = async () => {
      if (!albumId) return;

      try {
        setLoading(true);
        setError(null);

        let albumData = null;

        // Check if albumId can be fetched directly.
        // - UUIDs are our normal Lsnto album ids
        // - MassiveMusic hybrid search uses ids like `massive_<releaseId>`, which our API can ingest on-demand.
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(albumId);
        const isDirectMassiveId = typeof albumId === "string" && albumId.startsWith("massive_");
        const canFetchDirectly = isUUID || isDirectMassiveId;

        if (canFetchDirectly) {
          // Direct fetch by ID (UUID or MassiveMusic prefixed id)
          console.log(`Fetching album by ID: ${albumId}`);
          const res = await fetch(`${API_BASE_URL}/public/album/${albumId}`);
          
          if (!res.ok) {
            throw new Error(`Album not found (${res.status})`);
          }
          
          albumData = await res.json();
        } else {
          // Fetch by slug - need to search first
          console.log(`Searching for album by slug: ${albumId}`);
          
          let searchQuery = albumId;
          
          // If we have hierarchical route, we can be more specific
          if (isHierarchicalRoute && artistId) {
            // Search for albums by this artist
            const artistRes = await fetch(`${API_BASE_URL}/public/artist/${artistId}`);
            if (artistRes.ok) {
              const artistData = await artistRes.json();
              searchQuery = `${albumId} ${artistData.name}`;
            }
          }
          
          const searchRes = await fetch(`${API_BASE_URL}/public/search?q=${encodeURIComponent(searchQuery)}&type=album`);
          
          if (!searchRes.ok) {
            throw new Error('Album not found');
          }
          
          const searchData = await searchRes.json();
          
          if (!searchData.albums || searchData.albums.length === 0) {
            throw new Error('Album not found');
          }
          
          // Find the best match
          let bestMatch = null;
          const normalizedSlug = albumId.toLowerCase();
          
          for (const albumResult of searchData.albums) {
            // Check if slug matches exactly
            if (albumResult.slug && albumResult.slug.toLowerCase() === normalizedSlug) {
              bestMatch = albumResult;
              break;
            }
            
            // Check if title converted to slug matches
            const albumTitleSlug = albumResult.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            if (albumTitleSlug === normalizedSlug) {
              bestMatch = albumResult;
              break;
            }
          }
          
          if (!bestMatch) {
            bestMatch = searchData.albums[0];
          }
          
          // If we found a match, fetch the full album data
          const res = await fetch(`${API_BASE_URL}/public/album/${bestMatch.id}`);
          if (!res.ok) {
            throw new Error('Album not found');
          }
          
          albumData = await res.json();
          
          // If we're on a hierarchical route and found a different slug, redirect
          if (isHierarchicalRoute && bestMatch.slug && bestMatch.slug !== albumId) {
            const newUrl = `/artist/${artistId}/album/${bestMatch.slug}`;
            navigate(newUrl, { replace: true });
            return;
          }
        }

        // Validate we have album data
        if (!albumData) {
          throw new Error('Album not found');
        }

        console.log('Loaded album:', albumData);
        
        // Fetch tracks - ALWAYS show tracks for social sharing, but use bundle endpoint for access control
        let albumTracks: Track[] = [];
        
        // Try bundle endpoint first (for authenticated users with access control)
        if (user && token) {
          try {
            console.log(`🔍 Fetching tracks for album ${albumData.id} from bundle endpoint...`);
            const tracksData = await fetchAlbumTracks(albumData.id);
            console.log(`📦 Bundle response:`, tracksData);
            albumTracks = (tracksData.tracks || []).map((track: any): Track => {
              console.log(`🎵 Processing track:`, track);
              return {
                id: (track.id || track.track_id)?.toString(),
                title: track.title,
                duration: track.duration ?? undefined,
                track_number: track.track_number ?? undefined,
                file_path: track.file_path,
                preview_url: track.signed_url || track.preview_url || null,
                // Preserve access control fields for UI display and playback blocking
                access_type: track.access_type,
                access_granted: track.access_granted,
                access_reason: track.access_reason,
              };
            });
            console.log(`✅ Fetched ${albumTracks.length} tracks from bundle endpoint`);
          } catch (error: any) {
            console.error('❌ Error fetching album tracks from bundle:', error);
            console.warn('⚠️ Falling back to public endpoint for track metadata...');
          }
        }
        
        // Fallback to public endpoint for track metadata (tracks should ALWAYS be visible)
        // This ensures social sharing works and tracks are always displayed
        if (albumTracks.length === 0 && albumData.tracks && Array.isArray(albumData.tracks)) {
          console.log(`📋 Using tracks from public album endpoint (${albumData.tracks.length} tracks)`);
          const albumAccessType = albumData.access_type || 'standard';
          albumTracks = albumData.tracks.map((track: any): Track => ({
            id: track.id?.toString(),
            title: track.title,
            duration: track.duration ?? undefined,
            track_number: track.track_number ?? undefined,
            file_path: track.file_path,
            preview_url: track.preview_url || null,
            // Set access based on album type and user subscription
            access_type: albumAccessType,
            access_granted: albumAccessType === 'free' 
              ? true 
              : (albumAccessType === 'standard' ? hasActiveSubscription : false),
            access_reason: albumAccessType === 'standard' && !hasActiveSubscription 
              ? 'SUBSCRIPTION_REQUIRED' 
              : (albumAccessType !== 'free' && albumAccessType !== 'standard' ? 'PURCHASE_REQUIRED' : undefined),
          }));
        }
        
        // If still no tracks, try fetching from public endpoint directly
        if (albumTracks.length === 0) {
          try {
            console.log(`📋 Fetching tracks from public album endpoint as last resort...`);
            const publicAlbumRes = await fetch(`${API_BASE_URL}/public/album/${albumData.id}`);
            if (publicAlbumRes.ok) {
              const publicAlbumData = await publicAlbumRes.json();
              if (publicAlbumData.tracks && Array.isArray(publicAlbumData.tracks)) {
                console.log(`📋 Found ${publicAlbumData.tracks.length} tracks in public endpoint`);
                const albumAccessType = albumData.access_type || 'standard';
                albumTracks = publicAlbumData.tracks.map((track: any): Track => ({
                  id: track.id?.toString(),
                  title: track.title,
                  duration: track.duration ?? undefined,
                  track_number: track.track_number ?? undefined,
                  file_path: track.file_path,
                  preview_url: track.preview_url || null,
                  // Set access based on album type and user subscription
                  access_type: albumAccessType,
                  access_granted: albumAccessType === 'free' 
                    ? true 
                    : (albumAccessType === 'standard' ? hasActiveSubscription : false),
                  access_reason: albumAccessType === 'standard' && !hasActiveSubscription 
                    ? 'SUBSCRIPTION_REQUIRED' 
                    : (albumAccessType !== 'free' && albumAccessType !== 'standard' ? 'PURCHASE_REQUIRED' : undefined),
                }));
              }
            }
          } catch (error) {
            console.error('Error fetching tracks from public endpoint:', error);
          }
        }
        
        // Set album with tracks (always show tracks for social sharing)
        setAlbum({
          ...albumData,
          tracks: albumTracks.length > 0 ? albumTracks : undefined,
        });

        // Check access permissions
        if (user && token) {
          try {
            const access = await canPlayAlbum(albumData.id, albumData.access_type || 'standard', token);
            setAlbumAccess(access);
          } catch (error) {
            console.error('Error checking album access:', error);
          }
        }

      } catch (err) {
        console.error('Error loading album:', err);
        setError(err instanceof Error ? err.message : 'Failed to load album information');
      } finally {
        setLoading(false);
      }
    };

    loadAlbum();
  }, [albumId, artistId, isHierarchicalRoute, navigate, user, token]);

  // Auto-play track from URL parameter (e.g., shared links)
  useEffect(() => {
    if (!album || !album.tracks || autoPlayTriggered) return;

    // Check for track parameter in URL
    const urlParams = new URLSearchParams(location.search);
    const trackIdFromUrl = urlParams.get('track');

    if (trackIdFromUrl) {
      // Find the track in the album
      const trackToPlay = album.tracks.find(t => t.id === trackIdFromUrl);
      
      if (trackToPlay) {
        console.log('Auto-playing track from share link:', trackToPlay.title);
        setAutoPlayTriggered(true);
        
        // Small delay to ensure everything is loaded
        setTimeout(() => {
          handleTrackClick(trackToPlay);
        }, 500);
      }
    }
  }, [album, autoPlayTriggered, location.search]);

  const fetchSignedTrackUrl = async (track: Track): Promise<string> => {
    // Check access BEFORE fetching URL
    const trackAccessType = (track as any).access_type || album?.access_type || 'standard';
    const trackAccessGranted = (track as any).access_granted === true;
    
    // For standard tracks, require active subscription
    if (trackAccessType === 'standard') {
      if (!hasActiveSubscription) {
        throw new Error('SUBSCRIPTION_REQUIRED');
      }
      // Even with subscription, if API explicitly denied access, block
      if ((track as any).access_granted === false) {
        throw new Error((track as any).access_reason || 'ACCESS_DENIED');
      }
    }
    
    // For premium tracks, require explicit access_granted === true
    if (trackAccessType === 'soundcheck' || trackAccessType === 'vip' || trackAccessType === 'limited') {
      if (!trackAccessGranted) {
        throw new Error('PURCHASE_REQUIRED');
      }
    }
    
    // For free tracks, allow if access_granted is not explicitly false
    if (trackAccessType === 'free' && (track as any).access_granted === false) {
      throw new Error((track as any).access_reason || 'ACCESS_DENIED');
    }
    
    if (!user || !token) {
      try {
        if (track.preview_url && track.preview_url.startsWith('http')) {
          return track.preview_url;
        }
        
        if (track.file_path) {
          const response = await fetch(
            `${API_BASE_URL}/media/preview?filePath=${encodeURIComponent(track.file_path)}`
          );
          
          if (response.ok) {
            const data = await response.json();
            return data.signedUrl || "";
          }
        }
        
        throw new Error('AUTHENTICATION_REQUIRED');
      } catch (error) {
        console.error("Error fetching preview URL:", error);
        throw error;
      }
    }
    
    try {
      // For MassiveMusic tracks, file_path is NULL, so use track ID instead
      const filePathOrId = track.file_path || track.id;
      
      if (filePathOrId) {
        const response = await fetch(
          `${API_BASE_URL}/media/audio?filePath=${encodeURIComponent(filePathOrId)}`,
          { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } }
        );
        
        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('ACCESS_DENIED');
          }
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        return data.signedUrl || "";
      }
      
      if (track.preview_url && track.preview_url.startsWith('http')) {
        return track.preview_url;
      }
      
      throw new Error('NO_AUDIO_URL');
    } catch (error) {
      console.error("Error fetching signed URL:", error);
      throw error;
    }
  };

  const handleAddToQueue = (track: Track, e: React.MouseEvent) => {
    e.stopPropagation();
    if (user && albumAccess && !albumAccess.canPlay) {
      alert(albumAccess.reason || 'Access denied');
      return;
    }
    console.log('Queue track', track.title);
  };

  const handleTrackClick = async (track: Track) => {
    if (!album) return;

    if (currentTrack?.id === track.id) {
      togglePlay();
      return;
    }

    // Check track-level access control (from bundle endpoint)
    const trackAccessType = (track as any).access_type || album.access_type || 'standard';
    const trackAccessGranted = (track as any).access_granted === true;
    
    // For standard tracks, require active subscription
    if (trackAccessType === 'standard') {
      if (!hasActiveSubscription) {
        alert('Active subscription required to play this track.');
        navigate('/subscribe');
        return;
      }
      // Even with subscription, if API explicitly denied access, block playback
      if ((track as any).access_granted === false) {
        alert((track as any).access_reason || 'Access denied');
        return;
      }
    }
    
    // For premium tracks (soundcheck/vip/limited), require explicit access_granted === true
    if (trackAccessType === 'soundcheck' || trackAccessType === 'vip' || trackAccessType === 'limited') {
      if (!trackAccessGranted) {
        alert('This track requires purchase. Please visit the album page to purchase access.');
        navigate(`/album/${album.id}`);
        return;
      }
    }
    
    // For free tracks, allow playback (access_granted should be true)
    if (trackAccessType === 'free' && !trackAccessGranted) {
      alert((track as any).access_reason || 'Access denied');
      return;
    }

    // Check album-level access if user is logged in
    if (user && albumAccess && !albumAccess.canPlay) {
      if (album.access_type === 'free' || album.access_type === 'standard') {
        alert(albumAccess.reason || 'Access denied');
      } else {
        navigate(`/checkout/album/${album.id}`);
      }
      return;
    }

    let audioUrl: string;
    try {
      audioUrl = await fetchSignedTrackUrl(track);
      if (!audioUrl) {
        console.error("Failed to get audio URL for track:", track.id);
        return;
      }
    } catch (error: any) {
      console.error("Error getting audio URL:", error);
      const errorMsg = error.message || 'Failed to get audio URL';
      
      if (errorMsg === 'SUBSCRIPTION_REQUIRED') {
        alert('Active subscription required to play this track.');
        navigate('/subscribe');
      } else if (errorMsg === 'PURCHASE_REQUIRED') {
        alert('This track requires purchase. Please visit the album page to purchase access.');
        navigate(`/album/${album.id}`);
      } else if (errorMsg === 'AUTHENTICATION_REQUIRED') {
        alert('Please log in to play tracks.');
        navigate('/login');
      } else {
        alert(errorMsg || 'Access denied');
      }
      return;
    }
    
    const tracksWithUrl = (album.tracks || []).map(t => ({
      ...t,
      preview_url: t.id === track.id ? audioUrl : (t.preview_url || ""),
      albumArtwork: album.artwork_url || PLACEHOLDER_ALBUM,
      artwork: album.artwork_url || PLACEHOLDER_ALBUM,
      albumName: album.title,
      artistName: album.artist?.name,
      artist: album.artist?.name,
      artistProfileImage: album.artist?.image_url
    }));
    
    playTrack({
      ...track,
      preview_url: audioUrl,
      albumArtwork: album.artwork_url || PLACEHOLDER_ALBUM,
      artwork: album.artwork_url || PLACEHOLDER_ALBUM,
      albumName: album.title,
      artistName: album.artist?.name,
      artist: album.artist?.name,
      artistProfileImage: album.artist?.image_url
    }, tracksWithUrl);
  };

  const handlePlayAlbum = async () => {
    if (!album || !album.tracks || album.tracks.length === 0) {
      alert('No tracks available for this album');
      return;
    }

    const firstTrack = album.tracks[0];
    if (firstTrack) {
      await handleTrackClick(firstTrack);
    }
  };

  const handleShare = async () => {
    if (navigator.share && album) {
      try {
        await navigator.share({
          title: album.title,
          text: `"${album.title}" by ${album.artist?.name} – Real music by real artists on Lsnto`,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const goBack = () => navigate(-1);

  const getAccessTypeColor = (accessType: string) => {
    switch (accessType) {
      case 'free': return 'success';
      case 'standard': return 'primary';
      case 'soundcheck': return 'warning';
      case 'vip': return 'error';
      case 'limited': return 'secondary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !album) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="60vh"
          textAlign="center"
        >
          <Typography variant="h5" color="error" gutterBottom>
            {error || "Album not found"}
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="contained"
            onClick={goBack}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  const canPlay = !user || !albumAccess || albumAccess.canPlay;
  const accessType = album.access_type || 'standard';

  return (
    <MetaTagManager data={createAlbumMetaData({
      title: album.title,
      artist_name: album.artist?.name,
      artwork_url: album.artwork_url,
      description: `Listen to ${album.title} by ${album.artist?.name} on Lsnto.`
    })}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
        {/* Mobile Back Button */}
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <IconButton onClick={goBack} sx={{ color: 'text.primary' }}>
              <ArrowBackIcon />
            </IconButton>
          </Box>
        )}

        {/* Album Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 4 },
          mb: 4 
        }}>
          {/* Album Artwork */}
          <Box sx={{ 
            alignSelf: { xs: 'center', md: 'flex-start' },
            position: 'relative'
          }}>
            <Card sx={{ 
              width: { xs: 280, sm: 320, md: 300 }, 
              height: { xs: 280, sm: 320, md: 300 },
              boxShadow: 3
            }}>
              <CardMedia
                component="img"
                image={album.artwork_url || PLACEHOLDER_ALBUM}
                alt={album.title}
                sx={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover' 
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = PLACEHOLDER_ALBUM;
                }}
              />
            </Card>
            
            {/* Play Button Overlay */}
            {album.tracks && album.tracks.length > 0 && (
              <IconButton
                onClick={handlePlayAlbum}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  width: 56,
                  height: 56,
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                {isPlaying && currentTrack?.albumName === album.title ? (
                  <PauseIcon fontSize="large" />
                ) : (
                  <PlayArrowIcon fontSize="large" />
                )}
              </IconButton>
            )}
          </Box>

          {/* Album Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2}>
              {/* Access Type Badge */}
              {accessType !== 'standard' && (
                <Box>
                  <Chip 
                    label={accessType.toUpperCase()} 
                    color={getAccessTypeColor(accessType)} 
                    size="small" 
                  />
                </Box>
              )}

              {/* Album Title */}
              <Typography 
                variant={isMobile ? "h4" : "h3"} 
                component="h1"
                sx={{ 
                  fontWeight: 'bold',
                  wordBreak: 'break-word'
                }}
              >
                {album.title}
              </Typography>

              {/* Artist Name */}
              {album.artist && (
                <Typography 
                  variant="h6" 
                  color="text.secondary"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/artist/${(album.artist && 'slug' in album.artist && album.artist.slug) || album.artist?.id}`)}
                >
                  {album.artist.name}
                </Typography>
              )}

              {/* Release Date */}
              {album.release_date && (
                <Typography variant="body1" color="text.secondary">
                  Released: {new Date(album.release_date).toLocaleDateString()}
                </Typography>
              )}

              {/* Track Count */}
              {album.tracks && (
                <Typography variant="body2" color="text.secondary">
                  {album.tracks.length} track{album.tracks.length !== 1 ? 's' : ''}
                </Typography>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                {!canPlay && (
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => navigate(`/checkout/album/${album.id}`)}
                  >
                    Purchase
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                >
                  Share
                </Button>
              </Stack>

              {/* Access Warning */}
              {user && albumAccess && !albumAccess.canPlay && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {albumAccess.reason || 'This album requires a subscription to play.'}
                </Alert>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Track List */}
        {album.tracks && album.tracks.length > 0 ? (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Tracks
            </Typography>
            
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
              {album.tracks.map((track, index) => {
                const isCurrentTrack = currentTrack?.id === track.id;
                const isTrackPlaying = isPlaying && isCurrentTrack;
                const isRestricted = track.access_type === 'soundcheck' && track.access_granted !== true;
                
                return (
                  <ListItem key={track.id} disablePadding>
                    <ListItemButton 
                      onClick={() => handleTrackClick(track)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        ...(isCurrentTrack && {
                          backgroundColor: 'action.selected',
                        })
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                        <Box sx={{ width: 32, textAlign: 'center' }}>
                          {isRestricted ? (
                            <LockIcon color="disabled" fontSize="small" />
                          ) : isTrackPlaying ? (
                            <PauseIcon color="primary" fontSize="small" />
                          ) : isCurrentTrack ? (
                            <PlayArrowIcon color="primary" fontSize="small" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {track.track_number || index + 1}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <Typography 
                              variant="body1" 
                              noWrap
                              sx={{ 
                                fontWeight: isCurrentTrack ? 'bold' : 'normal',
                                color: isRestricted ? 'text.disabled' : (isCurrentTrack ? 'primary.main' : 'text.primary')
                              }}
                            >
                              {track.title}
                            </Typography>
                            {track.explicit && (
                              <ExplicitIcon fontSize="small" color="action" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {album.artist?.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 56, textAlign: 'right' }}>
                          {formatDuration(track.duration)}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTrackClick(track);
                            }}
                          >
                            {isTrackPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" onClick={(e) => handleAddToQueue(track, e)}>
                            <QueueIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMixtapeDialog({ open: true, trackId: String(track.id) });
                            }}
                          >
                            <AddToPlaylistIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ) : user && token ? (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No tracks available. This may require a subscription.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 4 }}>
            <Alert severity="info">
              Please log in to view tracks for this album.
            </Alert>
          </Box>
        )}
        <AddToMixtapeDialog
          open={mixtapeDialog.open}
          trackId={mixtapeDialog.trackId}
          onClose={() => setMixtapeDialog({ open: false })}
        />
      </Container>
    </MetaTagManager>
  );
};

export default AlbumPageBySlug; 
